import { FC } from 'react';
import { Card } from '../components/Card';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { sortByNewest } from '../utils/arrays';
import { IonButton, IonButtons, IonSkeletonText } from '@ionic/react';
import { ICaremapNoteResponse } from '../notes/model/NotesClient';
import { IRecording, RecordingState } from '../appointments/recording/RecordingState';
import { ViewNoteWithQueryParamDrawer } from '../notes/view-note/ViewNoteWithQueryParamDrawer';
import { Text } from '../components/Text';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CareMapState } from '../caremap/CareMapState';
import { NotesModel } from '../notes/model/NotesModel';
import { CareMapNotesListItem } from '../notes/note-list/CareMapNotesListItem';
import { Recorder } from '../appointments/recording/Recorder';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';

const EmptyStateContent: FC = () => {
    const history = useInstance(HistoryViewModel);
    const careMapState = useInstance(CareMapState);
    const recordingState = useInstance(RecordingState);

    const careMap = careMapState.getMostRecentCaremap();
    const isViewer = careMap && !careMap?.links?.self?.actions.includes('put');

    const addRecipient = () => {
        history.push('/q/new-care-recipient');
    };

    return (
        <Recorder trackingFrom="Record Widget" cannotRecordClickHandler={addRecipient}>
            <div
                style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}
                onClick={() => recordingState.updateCurrentRecordingExtras({initiatedFrom: 'Empty State Card'})}
            >
                <div style={{marginTop: 20, fontSize: 16}}>
                    <b>Be present, we’ll take notes!</b>
                </div>
                <div style={{marginTop: 20, marginBottom: 32, maxWidth: '50%'}}>
                    <img style={{width: '100%', height: 'auto', display: 'block'}} src="/assets/images/recordings/no-recordings-image.svg" alt="" />
                </div>
                <div style={{textAlign: 'center', marginBottom: 32, maxWidth: 300}}>
                    Record your next care conversation to get a
                    <b> shareable summary</b> and a <b>clear action plan</b>.
                </div>
                <div style={{marginBottom: 22}}>
                    {isViewer ? (
                        <div style={{textAlign: 'center', marginBottom: 32, maxWidth: 300}}>
                            You do not have permission to add recordings for {careMapState.getPatientFullName()}.&nbsp;<div style={{display: 'inline-block', cursor: 'pointer', color: 'var(--ion-color-primary)'}} onClick={addRecipient}>Add</div> or switch to another care recipient to start recording.
                        </div>
                    ) : (
                        <><b>Tap</b> to record!</>
                    )}
                </div>
            </div>
        </Recorder>
    );
};

export const Recordings: FC = () => {
    const history = useInstance(HistoryViewModel);
    const recordingState = useInstance(RecordingState);
    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);
    const careMapState = useInstance(CareMapState);

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    // TODO: forActiveCaremap should maybe return underined or something instead of throwing an error?
    //      we want this to show up if the user is anonymous still
    let notesModel: NotesModel;
    try {
        notesModel = notesModelFactory.forActiveCaremap();
    } catch (e) {
        // no active caremap
        return <Card padded><EmptyStateContent /></Card>;
    }

    const numToShow = 3;
    const sortedNotes: ICaremapNoteResponse[] = sortByNewest(notesModel.getNotes().filter(n => n.otherAttributes.recording), 'updatedAt');

    return (
        <>
            {careMapState.getCareMaps().length > 1 &&
                <div style={{margin: '10px 10px 20px'}}>
                    <CareRecipientSwitcher />
                </div>
            }

            <Card >
                {sortedNotes.length > 0 && (
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, paddingTop: 16}}>
                        <div style={{paddingLeft: 16}}><Text><b>Recent Summaries</b></Text></div>
                        <IonButtons slot="end">
                            <div style={{marginRight: 12}}>
                                <IonButton
                                    fill="clear"
                                    color="primary"
                                    data-id="view-all-recordings"
                                    onClick={() => history.push(`/notes/${careMapState.getActiveCaremap()?.careMapId}`)}
                                >
                                    View All
                                </IonButton>
                            </div>
                        </IonButtons>
                    </div>
                )}

                {recordingState.isRecording() || notesModel.hasLoaded ? sortedNotes.length ? sortedNotes.slice(0, numToShow).map((note: ICaremapNoteResponse, idx) => {
                    const recording = note.otherAttributes.recording as IRecording;
                    const uploaded = recordingState.isUploadedStatus(recording.status);
                    return (
                        <CareMapNotesListItem
                            note={note}
                            key={note.noteId}
                            button={uploaded}
                            detail={uploaded}
                            showPrivacy={false}
                            showNoteTargetHeader
                            lines={idx === numToShow-1 ? 'none' : undefined}
                            onClick={uploaded ? () => viewNoteDrawer.open({ noteId: note.noteId, trackingFrom: 'Recent Recordings' }) : undefined}
                        />
                    );
                }) : (
                    <EmptyStateContent />
                ) : (
                    <div style={{margin: 12, display: 'flex', flexDirection: 'column', gap: 8}}>
                        {[1,2,3].map(i => (
                            <IonSkeletonText key={i} animated style={{'--border-radius': '8px', height: 75}}></IonSkeletonText>
                        ))}
                    </div>
                )}
            </Card>
        </>
    );
};
